import React, { useState, useEffect } from "react";
import { CheckCircleIcon, ArrowPathIcon, ClipboardDocumentIcon, ExclamationCircleIcon, TrophyIcon, XMarkIcon, ChevronDownIcon, FunnelIcon } from "@heroicons/react/24/solid";

export default function AdminLotteryResults() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [filter, setFilter] = useState('winners');
  const [matchFilter, setMatchFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  useEffect(() => {
    loadResults();
  }, []);

  useEffect(() => {
    if (copiedAddress) {
      const timer = setTimeout(() => {
        setCopiedAddress(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedAddress]);

  const loadResults = async () => {
    setLoading(true);
    
    try {
      const response = await fetch('/api/admin/lottery-results');
      const data = await response.json();
      setResults(data.results || []);
      setError("");
    } catch (err) {
      console.error("Failed to load results:", err);
      setError("Failed to load lottery results");
    } finally {
      setLoading(false);
    }
  };

  const processWinnerPayout = async (resultId) => {
    setProcessingId(resultId);
    
    try {
      const response = await fetch('/api/admin/lottery-results', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          resultId,
          status: 'completed'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to process payout');
      }
      
      setResults(results.map(result => 
        result._id === resultId 
          ? { ...result, status: 'completed' }
          : result
      ));
    } catch (err) {
      console.error("Failed to process payout:", err);
      setError("Failed to process winner payout");
    } finally {
      setProcessingId(null);
    }
  };

  const copyToClipboard = (address, id) => {
    navigator.clipboard.writeText(address)
      .then(() => {
        setCopiedAddress(id);
      })
      .catch(err => {
        console.error("Failed to copy:", err);
        setError("Failed to copy wallet address");
      });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  const getPrizeTypeLabel = (prizeType, matchCount) => {
    if (prizeType === 'no_win') {
      return { label: `${matchCount} Match${matchCount !== 1 ? 'es' : ''}`, color: 'text-gray-400 bg-gray-900/50' };
    }
    
    switch (prizeType) {
      case 'jackpot':
        return { label: '🏆 JACKPOT', color: 'text-yellow-400 bg-gradient-to-r from-yellow-900/50 to-amber-900/50' };
      case '5_matches':
        return { label: '✨ 5 Matches', color: 'text-purple-400 bg-gradient-to-r from-purple-900/50 to-violet-900/50' };
      case '4_matches':
        return { label: '💎 4 Matches', color: 'text-blue-400 bg-gradient-to-r from-blue-900/50 to-cyan-900/50' };
      case '3_matches':
        return { label: '🎯 3 Matches', color: 'text-green-400 bg-gradient-to-r from-green-900/50 to-emerald-900/50' };
      default:
        return { label: prizeType, color: 'text-gray-400 bg-gray-900/50' };
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-300 bg-yellow-900/50';
      case 'completed':
        return 'text-green-300 bg-green-900/50';
      case 'failed':
        return 'text-red-300 bg-red-900/50';
      case 'no_payout_needed':
        return 'text-gray-500 bg-gray-900/50';
      default:
        return 'text-gray-300 bg-gray-900/50';
    }
  };

  const filteredResults = results.filter(result => {
    if (filter === 'winners' && !result.isWinner) return false;
    if (filter === 'pending' && result.status !== 'pending') return false;
    if (matchFilter !== 'all' && result.matchCount !== parseInt(matchFilter)) return false;
    return true;
  });

  const stats = {
    totalParticipants: results.length,
    totalWinners: results.filter(r => r.isWinner).length,
    pendingPayouts: results.filter(r => r.status === 'pending').length,
    totalPendingAmount: results.filter(r => r.status === 'pending').reduce((sum, r) => sum + r.prizeAmount, 0),
  };

  if (loading && results.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center px-4">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-gray-700"></div>
            <div className="absolute inset-0 rounded-full border-4 border-yellow-400 border-t-transparent animate-spin"></div>
            <TrophyIcon className="absolute inset-4 text-yellow-400" />
          </div>
          <p className="text-gray-400 text-sm">Loading lottery results...</p>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white pb-20">
      {/* Header - Fixed */}
      <div className="sticky top-0 z-40 bg-gray-900/95 backdrop-blur-lg border-b border-gray-800">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <TrophyIcon className="h-6 w-6 text-yellow-400" />
              <h1 className="text-lg font-bold">Lottery Admin</h1>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-all ${
                  showFilters ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300'
                }`}
              >
                <FunnelIcon className="h-4 w-4" />
              </button>
              <button 
                onClick={loadResults}
                className="p-2 bg-gray-800 text-gray-300 rounded-lg transition-all active:scale-95"
              >
                <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Stats Cards - Horizontal Scroll */}
          <div className="flex space-x-3 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            <div className="flex-shrink-0 bg-gradient-to-br from-gray-800 to-gray-900 p-3 rounded-xl border border-gray-700 min-w-[140px]">
              <div className="text-xs text-gray-400 mb-1">Participants</div>
              <div className="text-xl font-bold">{stats.totalParticipants}</div>
            </div>
            <div className="flex-shrink-0 bg-gradient-to-br from-green-900/30 to-emerald-900/30 p-3 rounded-xl border border-green-800/50 min-w-[140px]">
              <div className="text-xs text-green-400 mb-1">Winners</div>
              <div className="text-xl font-bold text-green-400">{stats.totalWinners}</div>
            </div>
            <div className="flex-shrink-0 bg-gradient-to-br from-yellow-900/30 to-amber-900/30 p-3 rounded-xl border border-yellow-800/50 min-w-[140px]">
              <div className="text-xs text-yellow-400 mb-1">Pending</div>
              <div className="text-xl font-bold text-yellow-400">{stats.pendingPayouts}</div>
            </div>
            <div className="flex-shrink-0 bg-gradient-to-br from-orange-900/30 to-red-900/30 p-3 rounded-xl border border-orange-800/50 min-w-[140px]">
              <div className="text-xs text-orange-400 mb-1">Amount</div>
              <div className="text-lg font-bold text-orange-400">{stats.totalPendingAmount.toLocaleString()}</div>
              <div className="text-xs text-orange-400/70">ARENA</div>
            </div>
          </div>
        </div>

        {/* Filters - Collapsible */}
        {showFilters && (
          <div className="px-4 pb-4 border-t border-gray-800 mt-2 pt-4 animate-in slide-in-from-top">
            <div className="space-y-3">
              {/* Status Filter */}
              <div>
                <p className="text-xs text-gray-400 mb-2">Status Filter</p>
                <div className="grid grid-cols-3 gap-2">
                  {['all', 'winners', 'pending'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setFilter(status)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        filter === status 
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' 
                          : 'bg-gray-800 text-gray-300'
                      }`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Match Filter */}
              <div>
                <p className="text-xs text-gray-400 mb-2">Match Filter</p>
                <div className="grid grid-cols-5 gap-2">
                  {['all', '3', '4', '5', '6'].map((matches) => (
                    <button
                      key={matches}
                      onClick={() => setMatchFilter(matches)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        matchFilter === matches 
                          ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30' 
                          : 'bg-gray-800 text-gray-300'
                      }`}
                    >
                      {matches === 'all' ? 'All' : matches}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-900/30 backdrop-blur-sm border border-red-700/50 rounded-xl flex items-center text-red-200 text-sm">
          <ExclamationCircleIcon className="h-5 w-5 mr-2 text-red-400 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Results Count */}
      <div className="px-4 py-3">
        <p className="text-sm text-gray-400">
          Showing <span className="text-white font-medium">{filteredResults.length}</span> results
        </p>
      </div>

      {/* Results List */}
      <div className="px-4 space-y-3">
        {filteredResults.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-800 mb-4">
              <XMarkIcon className="h-8 w-8 text-gray-600" />
            </div>
            <p className="text-gray-400">No results match your filters</p>
            <button 
              onClick={() => {setFilter('all'); setMatchFilter('all');}}
              className="mt-4 text-sm text-blue-400 underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          filteredResults.map((result) => {
            const prizeInfo = getPrizeTypeLabel(result.prizeType, result.matchCount);
            const isExpanded = expandedCard === result._id;
            
            return (
              <div 
                key={result._id} 
                className={`rounded-2xl overflow-hidden transition-all duration-300 ${
                  result.isWinner 
                    ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90 border border-green-700/50 shadow-lg shadow-green-900/20' 
                    : 'bg-gray-800/50 border border-gray-700/50'
                }`}
              >
                {/* Card Header - Always Visible */}
                <div 
                  onClick={() => setExpandedCard(isExpanded ? null : result._id)}
                  className="p-4 cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="text-sm font-medium text-white truncate">{result.userEmail}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{formatDate(result.drawDate)}</p>
                    </div>
                    <div className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${getStatusBadge(result.status)}`}>
                      {result.status.replace('_', ' ')}
                    </div>
                  </div>

                  {/* Prize Badge */}
                  <div className="flex items-center justify-between">
                    <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${prizeInfo.color}`}>
                      {prizeInfo.label}
                      {result.isSharedJackpot && (
                        <span className="ml-1 text-xs opacity-75">
                          (÷{result.totalJackpotWinners})
                        </span>
                      )}
                    </div>
                    <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>

                  {/* Prize Amount - Show for winners */}
                  {result.isWinner && (
                    <div className="mt-3 flex items-baseline justify-between">
                      <span className="text-xs text-gray-400">Prize:</span>
                      <span className="text-lg font-bold text-yellow-400">
                        {result.prizeAmount.toLocaleString()} ARENA
                      </span>
                    </div>
                  )}
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-4 animate-in slide-in-from-top duration-200">
                    {/* Numbers */}
                    <div className="bg-gray-900/50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-2">Ticket Numbers</p>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {result.ticketNumbers.map(num => (
                          <span 
                            key={num} 
                            className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                              result.winningNumbers.includes(num) 
                                ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/30' 
                                : 'bg-gray-800 text-gray-400'
                            }`}
                          >
                            {num}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-gray-800">
                        <span className="text-xs text-gray-400">Matches:</span>
                        <span className={`text-lg font-bold ${
                          result.matchCount >= 3 ? 'text-green-400' : 'text-gray-500'
                        }`}>
                          {result.matchCount}/6
                        </span>
                      </div>
                    </div>

                    {/* Wallet Address */}
                    <div className="bg-gray-900/50 rounded-xl p-3">
                      <p className="text-xs text-gray-400 mb-2">Wallet Address</p>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 font-mono text-xs text-gray-300 break-all">
                          {result.walletAddress}
                        </div>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(result.walletAddress, result._id);
                          }}
                          className="flex-shrink-0 p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-all active:scale-95"
                        >
                          {copiedAddress === result._id ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-400" />
                          ) : (
                            <ClipboardDocumentIcon className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Action Button */}
                    {result.status === 'pending' && result.isWinner && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          processWinnerPayout(result._id);
                        }}
                        disabled={processingId === result._id}
                        className={`w-full py-3 rounded-xl flex items-center justify-center text-sm font-medium transition-all ${
                          processingId === result._id
                            ? "bg-gray-700 text-gray-400"
                            : "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/30 active:scale-[0.98]"
                        }`}
                      >
                        {processingId === result._id ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                            Mark as Paid
                          </>
                        )}
                      </button>
                    )}

                    {result.status === 'completed' && result.processedAt && (
                      <div className="text-center text-xs text-gray-500">
                        Processed: {formatDate(result.processedAt)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add some bottom padding for better scrolling */}
      <div className="h-20"></div>
    </main>
  );
}
